import $ from 'jquery';
import { $body, $window, userIsAuthenticated } from 'util/global';
import {
  EVT_QUIZ_POSITION_WILL_CHANGE,
  EVT_QUIZ_POSITION_DID_CHANGE,
  EVT_QUIZ_POSITION_LOADING,
  EVT_VKEY_BEFORE_UPDATE,
  EVT_DAILYPROBLEM_MAINBODY_EXPANDED,
} from 'util/constants';

/** @type {boolean} */
let savedMediaUrl = false;

/**
 * A map of all of the visualizations encountered during a page load. This object will continue
 * being populated until the full page is refreshed. NOTE: "active" means that we've seen the
 * visualization, but it might not be loaded or rendered yet.
 * @type {Object.<string, Visualization>}
 */
const activeVisualizations = {};

/**
 * A list of all of the possible component keys that might contain a visualization.
 * @type {Array.<string>}
 */
const CONTENT_COMPONENT_KEYS = [
  'potw_problemset',
  'potw_problemset_default',
  'quizzes_problemset',
  'assessment_public_solvable_logged_in',
  'quizzes_interstitial_pane',
];

/**
 * Class name for the `stashed` visualization state. Positions the visualization outside of
 * the viewport
 */
const CLASS_NAME_STASHED = 'stashed';

/**
 * Class name for the `stashed` visualization state for diagrammars. Applies a default (and small)
 * width
 */
const CLASS_NAME_STASHED_FIXEDWIDTH = 'stashed-fixed-width';

/**
 * Create a detached DOM node containing the provided HTML. This allows us to efficiently
 * manipulate the HTML before adding to the page.
 * @param {string} html
 * @returns {JQuery}
 */
function createDetachedNode(html) {
  return $('<div />').html(html);
}

/**
 * Get the DP main body tail visualization container
 * @param {JQuery} key The visualization's key
 */
function $dailyproblemMainbodyTailViz(key) {
  return $(`.dp-mainbody-text .tail [data-visualizationkey=${key}]`);
}

/**
 * Determines whether or not to apply a small, fixed width when stashing.
 * We definitely DO want to do this for diagrammars, and to NOT do this for Graph2d.
 * It may be the case that we want this for all Elm interactives, and not for JS-based
 * ones, so implementing this as a separate function for the sake of easier changes to
 * the logic in the future.
 * @param {string} visualizationType The type of visualization
 * @returns {boolean} whether or not to apply the width
 */
function applyFixedWidthWhenStashing(vizType) {
  if (vizType === 'DIAGRAMMAR') {
    return true;
  }
  return false;
}

/**
 * @typedef VisualizationData
 * @prop {Object} visualizationData Data from the container data attributes
 * @prop {string} visualizationData.visualization Used by the visualization code to find its
 *                                                parent
 * @prop {string} visualizationData.src The URL for the visualization's HTML file
 * @prop {string} visualizationData.key The visualization's key
 * @prop {string} visualizationData.controller The visualization controller (unused outside
 *                                             of the generic controller system)
 */

/** @class Visualization state manager */
class VisualizationManager {
  /**
   * This is a class to manage the state of a visualization in the frontend. This determines
   * what to do when answering a problem or navigating within a quiz.
   * @param {JQuery} $container The visualization container from the controller
   * @param {VisualizationData} visualizationData
   */
  constructor($container, key, slug, visualizationData, visualizationType) {
    this.$container = $container;
    this.$content = this.$container.find('.content');
    this.key = key;
    this.slug = slug;
    this.data = visualizationData;
    this.fixWidthWhenStashing = applyFixedWidthWhenStashing(visualizationType);
    this.isInteractiveSolvable = visualizationData.solvableId && visualizationData.solvableId > 0;
    this.callbacks = null;

    // make these methods more functional (allows us to pass functions directly as args
    // instead of using anonymous functions)
    this.stash = this.stash.bind(this);

    this.initEventKeys();
    this.bindEvents();

    if (this.needsImmediateStash()) {
      this.stash();
    } else {
      this.waitUntilVisible().then(() => {});
    }
  }

  /** Set event namespace and event keys for this visualization */
  initEventKeys() {
    const eventNamespace = `viz${this.key}`;

    // Generate beforeupdate event keys for every possible component type
    const componentBeforeUpdateEvent = CONTENT_COMPONENT_KEYS.map(
      cmpKeyName => `${EVT_VKEY_BEFORE_UPDATE}.${cmpKeyName}.${eventNamespace}`,
    ).join(' ');

    const quizPositionLoadingEvent = `${EVT_QUIZ_POSITION_LOADING}.${eventNamespace}`;
    const quizPositionWillChangeEvent = `${EVT_QUIZ_POSITION_WILL_CHANGE}.${eventNamespace}`;

    const dpMainbodyExpanded = `${EVT_DAILYPROBLEM_MAINBODY_EXPANDED}.${eventNamespace}`;

    this.events = {
      quizPositionDidChange: `${EVT_QUIZ_POSITION_DID_CHANGE}.${eventNamespace}`,
      stashableEventGroup: [
        componentBeforeUpdateEvent,
        quizPositionLoadingEvent,
        quizPositionWillChangeEvent,
      ].join(' '),
      dpMainbodyExpanded,
    };
  }

  setCallbacks(callbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Resume an active visualization. This is called when the controller is called for an active
   * visualization.
   * @param {JQuery} $placeholderContainer The element passed to the controller method. In this
   *                                       case, this element is just a temporary placeholder
   *                                       for the stashed visualization.
   */
  resume($placeholderContainer) {
    if (this.callbacks && this.callbacks.setStashed) {
      this.callbacks.setStashed(false);
    }

    // unstash
    $placeholderContainer.replaceWith(this.$container);
    this.$container.removeClass(CLASS_NAME_STASHED);
    this.$container.removeClass('course-quiz-content');
    if (this.fixWidthWhenStashing) {
      this.$container.removeClass(CLASS_NAME_STASHED_FIXEDWIDTH);
    }

    this.bindEvents();
  }

  /** Setup events for this visualization. */
  bindEvents() {
    // Unstash when expanding Daily Problem
    $window.on(this.events.dpMainbodyExpanded, () => {
      $window.off(this.events.dpMainbodyExpanded);
      const $placeholderContainer = $dailyproblemMainbodyTailViz(this.key);
      if ($placeholderContainer.length) {
        this.resume($placeholderContainer);
      }
    });

    // No events necessary for logged out quizzes
    if (!userIsAuthenticated) {
      return;
    }

    // Stash when changing quiz position or answering a problem
    $window.on(this.events.stashableEventGroup, this.stash);
  }

  unbindEvents() {
    if (!userIsAuthenticated) {
      // No events necessary for logged out visualizations
      return;
    }

    $window.off(this.events.stashableEventGroup);
  }

  /**
   * Stash the visualization for later use. This moves the visualization to the body to prevent
   * some other process (component refresh, quiz nav) from clobbering the current visualization
   * state. This preserves events, animation, and any user-updated values.
   */
  stash() {
    const $placeholderContainer = createDetachedNode('');
    Object.keys(this.data)
      .filter(key => key.indexOf('v-') !== 0)
      .forEach(key => $placeholderContainer.attr(`data-${key}`, this.data[key]));
    if (!this.isInteractiveSolvable) {
      $placeholderContainer.attr('data-controller', 'app/visualizations:renderVisualization');
    }
    this.$container.after($placeholderContainer);

    if (this.callbacks && this.callbacks.setStashed) {
      this.callbacks.setStashed(true);
    }

    $body.append(this.$container);
    this.$container.addClass(CLASS_NAME_STASHED);
    this.$container.addClass('course-quiz-content');
    if (this.fixWidthWhenStashing) {
      this.$container.addClass(CLASS_NAME_STASHED_FIXEDWIDTH);
    }

    this.$container.find('script[type="math/tex"]').remove();
    this.unbindEvents();
  }

  needsImmediateStash() {
    // Visualization in DP hidden section
    return $dailyproblemMainbodyTailViz(this.key).length > 0;
  }

  /**
   * Wait until the visualizations is visible before performing an action. This is useful in
   * logged-out quizzes where we load all of the problems/panes for the quiz at once, but want
   * to wait to load the visualization when the user is viewing the problem/pane that it's on.
   */
  waitUntilVisible() {
    return new Promise(resolve => {
      // Make sure the container has a size before checking if it's visible
      setTimeout(() => {
        if (this.$container.is(':visible')) {
          resolve();
        } else {
          $window.on(this.events.quizPositionDidChange, () => {
            if (this.$container.is(':visible')) {
              resolve();
              $window.off(this.events.quizPositionDidChange);
            }
          });
        }
      }, 1);
    });
  }
}
/**
 * Import the required module(s) for a given visualization type
 * @param {string} visualizationType
 * @returns {Promise<{render: {Function}}>}
 */
export function getModuleForVisualizationType(visualizationType) {
  switch (visualizationType) {
    case 'GRAPH_2D':
      return import(/* webpackChunkName: 'viz-02-graph' */ 'visualizations/apps/Graph2D');
    case 'GRAPH_3D':
      return import(/* webpackChunkName: 'viz-02-escher' */ 'visualizations/apps/Graph3D');
    case 'QUANTUM_CIRCUIT':
      return import(
        /* webpackChunkName: 'viz-02-quantum-circuit' */ 'visualizations/apps/QuantumCircuit'
      );
    case 'OPERATOR_SEARCH':
      return import(
        /* webpackChunkName: 'viz-02-operator-search' */ 'visualizations/apps/OperatorSearch'
      );
    case 'HUYGENS_CLOCK':
      return import(
        /* webpackChunkName: 'viz-02-huygens-clock' */ 'visualizations/apps/HuygensClock'
      );
    case 'COIN_FLIP':
      return import(/* webpackChunkName: 'viz-02-coinflip' */ 'visualizations/apps/CoinFlip');
    case 'CANVAS':
      return import(/* webpackChunkName: 'viz-02-canvas' */ 'visualizations/apps/Canvas');
    case 'LOGIC_CIRCUIT':
      return import(
        /* webpackChunkName: 'viz-02-logic-circuit' */ 'visualizations/apps/LogicCircuit'
      );
    case 'FREEFORM_JAVASCRIPT':
      return import(
        /* webpackChunkName: 'viz-02-javascript' */ 'visualizations/apps/FreeformJavascript'
      );
    case 'GREEDY_SCHEDULER':
      return import(
        /* webpackChunkName: 'viz-02-greedy-scheduler' */ 'visualizations/apps/GreedyScheduler'
      );
    case 'CS_ONE_OFFS':
      return import(/* webpackChunkName: 'viz-02-cs-one-offs' */ 'visualizations/apps/CsOneOffs');
    case 'DRONE_GAME':
      return import(/* webpackChunkName: 'viz-02-drone-game' */ 'visualizations/apps/DroneGame');
    case 'GRID_FILL':
      return import(/* webpackChunkName: 'viz-02-grid-fill' */ 'visualizations/apps/GridFill');
    case 'ORDER_LOGIC':
      return import(/* webpackChunkName: 'viz-02-order-logic' */ 'visualizations/apps/OrderLogic');
    case 'NUMBER_BUNCHERS':
      return import(
        /* webpackChunkName: 'viz-02-number-bunchers' */ 'visualizations/apps/NumberBunchers'
      );
    case 'CALCDOKU':
      return import(/* webpackChunkName: 'viz-02-calcdoku' */ 'visualizations/apps/Calcdoku');
    case 'SHERLOCK_SEARCH':
      return import(
        /* webpackChunkName: 'viz-02-sherlock-search' */ 'visualizations/apps/SherlockSearch'
      );
    case 'NEURAL_NETWORKS':
      return import(
        /* webpackChunkName: 'viz-02-neural-networks' */ 'visualizations/apps/NeuralNetworks'
      );
    case 'PARSONS_PROBLEM':
      return import(
        /* webpackChunkName: 'viz-02-parsons-problem' */ 'visualizations/apps/ParsonsProblem'
      );
    case 'HOT_COLD':
      return import(/* webpackChunkName: 'viz-02-hot-cold' */ 'visualizations/apps/HotCold');
    case 'CRYPTO_ONE_OFFS':
      return import(
        /* webpackChunkName: 'viz-02-crypto-one-offs' */ 'visualizations/apps/CryptoOneOffs'
      );
    case 'TOGGLE_TAGS':
      return import(/* webpackChunkName: 'viz-02-toggle-tags' */ 'visualizations/apps/ToggleTags');
    case 'DIAGRAMMAR':
      return import(/* webpackChunkName: 'viz-02-diagrammar' */ 'visualizations/apps/Diagrammar');
    case 'HEXAPAWN':
      return import(/* webpackChunkName: 'viz-02-hexapawn' */ 'visualizations/apps/Hexapawn');
    default:
      throw new Error(`Unknown visualization type '${visualizationType}'`);
  }
}

/**
 * Detect and prefetch visualization modules for preloaded quiz items
 * @param {string} textContent
 */
export function visualizationsPrefetchHandler(textContent) {
  // Matching `"type": "<visualization type>"` in the inline config object
  const visualizationRegex = /"type": ?"(.*?)"/g;
  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = visualizationRegex.exec(textContent)) !== null) {
    const [, possibleType] = match;
    // Webpack will only do this once per module, so no need to manually keep track of
    // which ones we've imported
    getModuleForVisualizationType(possibleType);
  }
}

/**
 * Render the visualization
 * @param {JQuery} $visualization
 * @param {boolean} [disableManager] - bypasses initializing a manager instance for the variable
 *                                     (only needed for non-admin web)
 * @param {boolean} [isInteractiveSolvable] - true if visualization is the target visualization in
 *                                            an interactive solvable
 * @returns {Promise<any | null>} - Returns a Promise that resolves an instance of the the
 *                                  visualization or null
 */
export function renderVisualization(
  $visualization,
  disableManager = false,
  isInteractiveSolvable = false,
  solvableConfig = {},
) {
  const vizData = $visualization.data();
  const key = vizData.visualizationkey;

  let visualizationManager = activeVisualizations[key];
  const $targetElement = isInteractiveSolvable
    ? $visualization.parents('.interactive')
    : $visualization;

  if (visualizationManager) {
    visualizationManager.resume($targetElement);
    return Promise.resolve();
    // eslint-disable-next-line no-else-return
  } else {
    // get the config from the script tag that's embedded in the visualization template
    const config = window[key];
    const userState = window[`${key}user_state__`] || {};

    if (!disableManager) {
      // cache the visualization state unless this is a preview
      visualizationManager = new VisualizationManager(
        $targetElement,
        key,
        config.slug,
        vizData,
        config.type,
      );
    }

    // put the media url in an accessible place
    if (!savedMediaUrl) {
      document.body.dataset.mediaBaseUrl = vizData.mediaurl;
      savedMediaUrl = true;
    }

    const visualizationInstancePromise = new Promise(resolve => {
      // render the visualization
      getModuleForVisualizationType(config.type).then(module => {
        const interactiveConfig = {
          slug: config.slug,
          visualizationKey: key,
          authoredData: config.authoredData,
          generatedData: config.generatedData,
          userState: userState.current_state || null,
          location: 'v2-quiz',
        };
        let callbacks = null;
        if (isInteractiveSolvable) {
          callbacks = module.default.createInteractiveSolvable(interactiveConfig, solvableConfig);
        } else {
          callbacks = module.default.createInteractive(interactiveConfig);
        }
        if (visualizationManager) {
          visualizationManager.setCallbacks(callbacks);
        }
        resolve(callbacks);
      });
    });
    activeVisualizations[key] = visualizationManager;
    return visualizationInstancePromise;
  }
}

export function interactiveProblemWrapper($container) {
  const key = $container.data('visualizationkey');
  const existingVisualization = activeVisualizations[key];
  if (existingVisualization) {
    $container.empty();
    const $child = createDetachedNode('');
    $container.append($child);
    existingVisualization.resume($child);
  }
}

/**
 * Render a visualization for use in a Vue component
 * @param {HTMLElement} element
 * @param {Object} props
 * @returns {Promise<any | null>} - Returns a Promise that resolves an instance of the the
 *                                  visualization or null
 */
export function renderVisualizationFromVue(element, props, solvableConfig) {
  Object.assign(element.dataset, props);
  const $visualization = $(element);
  return renderVisualization($visualization, false, true, solvableConfig);
}
